/**
 * Add New User (Create Patient Profile) — full-page flow for Collector users page.
 * Matches design: back link, status banner, invitation toggle, basic info, client tags, insurance, save/cancel.
 * Reuses MUI components from app and createNewUser / createBasicInformation / createPatientInsurance.
 */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldErrors, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { E164Number, parsePhoneNumber } from 'libphonenumber-js';
import { ValidationError } from 'yup';
import { toast } from 'react-toastify';
import {
  Alert,
  AlertTitle,
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Button,
  Link,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Container from '../../../../Components/Container/Container';
import { Loading } from '../../../../Components/LoadingSpinner/Loading';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import MultiSelectInputMUI from '@brands/Components/Inputs/MuiMultiSelectInput/MultiSelectInputMUI';
import ProfileImage from '@brands/Components/ProfileImage/ProfileImage';

import { listAllLanguages } from '../../../../services/identity/listAllLanguages';
import { listAllClientTags } from '../../../../services/identity/listAllClientTags';
import { UserRoleName } from '../../../../services/identity/types/UserProfile';
import { createCollectorUser, CreateCollectorUserPayload } from '../../../../services/identity/createCollectorUser';
import { createPatientInsurance } from '../../../../services/identity/createPatientInsurance';
import { getClientDivisionOptions } from '../../../../services/identity/clientDivisionOptions';
import { scopeDivisionOptionsToActor } from '../../../../services/identity/clientDivisionActorScope';
import { confirmAvatarMedia } from '../../../../services/identity/confirmAvatarMedia';
import { deleteAvatar } from '../../../../services/identity/deleteAvatar';
import { registerAvatarMedia } from '../../../../services/identity/registerAvatarMedia';
import { RegisterMediaPayload } from '@brands/services/communication/registersConversationMedia';
import { confirmOneReportMedia } from '@brands/services/oneReport/confirmOneReportMedia';
import { registerOneReportMedia } from '@brands/services/oneReport/registerOneReportMedia';
import { base64ListToFileList } from '@brands/Utils/base64ToFile';
import { getFileMimeType } from '@brands/Utils/truncateString';
import { BasicInfo, ClientTag, Insurance, UserProfile } from '../../../../services/identity/types/UserProfile';
import { dateAutoSlashes } from '../../../../Utils/checkAndSetDate';
import { displayErrorDetails } from '../../../../Utils/displayError';
import { genderIdentity, genderOptions, pronounsOptions } from '../../../../Utils/selectButtonOptions';
import { OptionLanguage, stateOptions } from '../../../../Utils/selectOptions';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';

import { defaultFormValues } from '../../../MyProfile/Patient/utils/defaultFormValues';
import { MyProfileSubmit } from '../../../MyProfile/Patient/utils/types';
import {
  updateData,
  updateDataInsuranceNewDesign,
  updateDataSecondaryInsuranceNewDesign,
  UploadedFile,
} from '../../../MyProfile/Patient/utils/utilsFunctions';
import { validationSchema } from '../../SuperAdmin/AddUserPages/Patient/validationSchema';

import styles from '../styles.module.scss';
import addNewUserStyles from './addNewUser.module.scss';
import { defaultInsuranceForm } from '../../../Insurance/utils/types';
import { validationSchemaNewDesign } from '../../../Insurance/utils/validationSchema';
import InsuranceFormSection from '../../../Insurance/InsuranceFormSection';
import { heicTo } from 'heic-to';

type FormValues = MyProfileSubmit &
  typeof defaultInsuranceForm & { client_tag_id?: string; client_division_id?: string };
type ClientTagOption = { id: string; value: string; label: string; case_types?: string[] };

const defaultValues: FormValues = {
  ...defaultFormValues,
  ...defaultInsuranceForm,
  preferred_name: '',
  client_tag_id: '',
  client_division_id: '',
} as FormValues;

const normalizeClientTagId = (value: unknown): string => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? String(id) : '';
};

const toClientTagOption = (tag: ClientTag): ClientTagOption => ({
  id: String(tag.id),
  value: String(tag.id),
  label: tag.name || 'Client tag',
  case_types: tag.case_types || [],
});

const resolveCollectorClientTags = (
  apiClientTags: ClientTag[] | undefined,
  authClientTags: ClientTag[] | undefined,
): ClientTag[] => {
  const validApiClientTags = (apiClientTags || []).filter((tag) => normalizeClientTagId(tag?.id));

  if (Array.isArray(authClientTags)) {
    const apiById = new Map(validApiClientTags.map((tag) => [String(tag.id), tag]));
    const seenIds = new Set<string>();

    return authClientTags.reduce<ClientTag[]>((acc, authTag) => {
      const id = normalizeClientTagId(authTag?.id);
      if (!id || seenIds.has(id)) return acc;
      seenIds.add(id);
      acc.push(apiById.get(id) || authTag);
      return acc;
    }, []);
  }

  return validApiClientTags;
};

const AddNewUserPage: React.FC = () => {
  const roleNavigate = useRoleNavigate();
  const { userInfo: authUser } = useAppSelector(selectAuth);
  const currentUserRole = authUser.role.name;
  const [sendInvitation, setSendInvitation] = useState<boolean>(true);
  const [addPrimaryInsurance, setAddPrimaryInsurance] = useState(false);
  const [addSecondaryInsurance, setAddSecondaryInsurance] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPayload, setAvatarPayload] = useState<RegisterMediaPayload | null>(null);
  const [avatarAction, setAvatarAction] = useState('');
  const [image, setImage] = useState<string>();
  const [languageOptions, setLanguageOptions] = useState<OptionLanguage[]>([]);
  const [clientTagOptions, setClientTagOptions] = useState<ClientTagOption[]>([]);
  const [allClientTags, setAllClientTags] = useState<ClientTag[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [images, setImages] = useState({
    front_of_card: [],
    back_of_card: [],
    secondary_front_of_card: [],
    secondary_back_of_card: [],
  } as any);
  // Required for every role once the field is actually on the page (mirrors the
  // `hasCollectorClientTags` visibility check on the Client tag select below) --
  // requiredness used to be gated to CareAdvisor/Collector/OrganizationAdmin only.
  const clientTagsRequired = useMemo(() => clientTagOptions.length > 0, [clientTagOptions]);
  // Insurance carrier options are handled inside `InsuranceFormSection`.
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
    resolver: async (values, context, options) => {
      const normalizedValues = { ...(values as FormValues) };

      // When insurance accordions are collapsed, coerce carrier ids to null
      // so Yup doesn't receive NaN and block submit.
      if (!addPrimaryInsurance) {
        normalizedValues.insurance_carrier_id = undefined;
      }
      if (!addSecondaryInsurance) {
        normalizedValues.secondary_insurance_carrier_id = undefined;
      }

      const isQuickAdd = !sendInvitation && !String(normalizedValues.email || '').trim();
      return yupResolver(
        validationSchema(addSecondaryInsurance, isQuickAdd, true, clientTagsRequired, false, divisionRequired),
      )(normalizedValues, context, options);
    },
  });

  useEffect(() => {
    listAllLanguages().then((data) => {
      setLanguageOptions(data.map((l) => ({ id: String(l.id), value: l.locale, label: l.name })));
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const applyClientTagOptions = (tags: ClientTag[]): void => {
      const options = tags.map(toClientTagOption);
      setClientTagOptions(options);
      setAllClientTags(tags);
      setValue('client_tag_id', options[0]?.value || '', { shouldValidate: true });
    };

    void (async () => {
      try {
        const { nodes } = await listAllClientTags(currentUserRole);
        if (cancelled) return;
        applyClientTagOptions(resolveCollectorClientTags(nodes, authUser?.clients));
      } catch (error) {
        if (cancelled) return;
        const fallbackClientTags = resolveCollectorClientTags([], authUser?.clients);
        applyClientTagOptions(fallbackClientTags);
        if (fallbackClientTags.length === 0) {
          displayErrorDetails(error);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authUser?.clients, setValue]);

  const allowedCollectorClientIds = useMemo(() => {
    const authClientTags = authUser?.clients;
    if (Array.isArray(authClientTags)) {
      return authClientTags
        .map((tag) => Number(normalizeClientTagId(tag.id)))
        .filter((id) => Number.isInteger(id) && id > 0);
    }

    return clientTagOptions
      .map((tag) => Number(normalizeClientTagId(tag.id)))
      .filter((id) => Number.isInteger(id) && id > 0);
  }, [authUser?.clients, clientTagOptions]);

  const triggerProfileAvatar = useCallback(
    (avatarUrl: string) => {
      setValue('profile_img', avatarUrl, { shouldValidate: true });
    },
    [setValue],
  );

  const uploadMedia = async (imagesValue: UploadedFile[] | undefined | null): Promise<string[]> => {
    if (!Array.isArray(imagesValue)) return [];

    const newUploadedImages = imagesValue.filter((img) => (img as any)?.dataURL?.startsWith('data:'));
    const existingImages = imagesValue.filter((img) => (img as any)?.dataURL?.startsWith('https://'));

    const base64List = newUploadedImages.map((img) => (img as any).dataURL as string);
    const fileList = await base64ListToFileList(base64List || []);
    const mediaIDs: string[] = [];

    if (newUploadedImages.length > 0 && Array.isArray(fileList)) {
      await Promise.all(
        Array.from(fileList).map(async (file, index) => {
          const img = newUploadedImages[index] as any;
          const mimeType = getFileMimeType(img.name);
          const payload: RegisterMediaPayload = { files: [{ filename: img.name, mimetype: mimeType }] };
          const registered = await registerOneReportMedia(payload, currentUserRole);
          const mediaId = registered.files[0].upload_id;
          await fetch(registered.files[0].upload_url, {
            method: 'PUT',
            body: file,
            headers: { 'content-type': mimeType },
          });
          await confirmOneReportMedia(mediaId, currentUserRole);
          mediaIDs.push(mediaId);
        }),
      );
    }

    if (existingImages.length > 0) {
      const existingIds = existingImages.map((img: any) => img.media_id).filter(Boolean) as string[];
      mediaIDs.push(...existingIds);
    }

    return mediaIDs;
  };

  const mapMediaToInsuranceMedia = (media: string[], type: string): { media_id: string; type: string }[] => {
    return media.map((mediaId) => ({ media_id: mediaId, type }));
  };

  const addImages = (files: File[], type: keyof typeof images): void => {
    const newImages = files.filter(
      (file) =>
        file.type.startsWith('image/') ||
        file.type === 'application/pdf' ||
        file.type === 'application/msword' ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'application/vnd.ms-powerpoint' ||
        file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    );

    if (((watch(type as any) as any[]) || []).length + newImages.length > 10) {
      displayErrorDetails('Max 10 files are allowed to upload.');
      return;
    }

    const imagesArray: UploadedFile[] = [];
    for (let i = 0; i < newImages.length; i++) {
      const file = newImages[i];
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.addEventListener('load', () => {
        imagesArray.push({ name: file.name, dataURL: reader.result as string });
        if (imagesArray.length === newImages.length) {
          setValue(type as any, imagesArray as any, { shouldValidate: true });
          setImages((prev: any) => ({ ...prev, [type]: imagesArray }));
        }
      });
    }
  };

  const deleteMedia = (mediaName: string, type: keyof typeof images): void => {
    const current = ((watch(type as any) as any[]) || []).filter((img) => img.name !== mediaName);
    setValue(type as any, current as any, { shouldValidate: true });
    setImages((prev: any) => ({ ...prev, [type]: (prev[type] || []).filter((img: any) => img.name !== mediaName) }));
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(event.target.files as unknown as File[]);
    const converted = await Promise.all(
      files.map(async (file) => {
        if (file.type === 'image/heic') {
          const jpegBlob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.85 });
          return new File([jpegBlob], file.name.replace(/\.heic$/i, '.jpeg'), { type: 'image/jpeg' });
        }
        return file;
      }),
    );
    addImages(converted, event.target.id as any);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>, type: any): Promise<void> => {
    event.preventDefault();
    setIsDragging(false);
    if (!event.dataTransfer.files?.length) return;
    let file = event.dataTransfer.files[0];
    if (file.type === 'image/heic') {
      const jpegBlob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.85 });
      file = new File([jpegBlob], file.name.replace(/\.heic$/i, '.jpeg'), { type: 'image/jpeg' });
    }
    addImages([file], type);
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const onSubmit = useCallback(
    async (data: FormValues) => {
      setIsSaving(true);
      let newUser: UserProfile | undefined;
      const dataWithDob = updateData(data as unknown as BasicInfo);
      const languageIds = ((data.language as OptionLanguage[]) || [])
        .map((l) => Number(l.id))
        .filter((n) => Number.isInteger(n) && !Number.isNaN(n));

      const fromFormId = Number(data.client_tag_id);
      if (
        allowedCollectorClientIds.length > 0 &&
        (!Number.isInteger(fromFormId) || !allowedCollectorClientIds.includes(fromFormId))
      ) {
        setError('client_tag_id', { type: 'manual', message: 'Please select a valid client tag.' });
        displayErrorDetails('Your client tag selection is no longer valid. Please select again.');
        setIsSaving(false);
        return;
      }
      const resolvedClientIdsForCreate = allowedCollectorClientIds.length === 0 ? [] : [fromFormId];
      const fromFormDivisionId = Number(data.client_division_id);
      const resolvedClientDivisionIdsForCreate = Number.isInteger(fromFormDivisionId) ? [fromFormDivisionId] : [];

      const createPayload: CreateCollectorUserPayload = {
        email: data.email?.toLowerCase() || undefined,
        type: UserRoleName.Patient,
        organization_ids: ['dentistry-one'],
        division_ids: [],
        practice_ids: [],
        send_invitation: sendInvitation,
        is_quick_add: !sendInvitation && !String(data.email || '').trim(),
        full_name: sendInvitation ? `${data.first_name ?? ''} ${data.last_name ?? ''}`.trim() : undefined,
        frontend_website: window.location.origin,
        basic_info: {
          first_name: data.first_name ?? '',
          middle_name: data.middle_name ?? '',
          last_name: data.last_name ?? '',
          preferred_name: data.preferred_name ?? '',
          date_of_birth:
            dataWithDob.date_of_birth && dataWithDob.date_of_birth !== 'Invalid date'
              ? dataWithDob.date_of_birth
              : null,
          gender: data.gender ?? '',
          gender_identity: data.gender_identity ?? '',
          pronouns: data.pronouns ?? '',
          languages: languageIds,
        } as unknown as CreateCollectorUserPayload['basic_info'],
        address: {
          address1: data.address1 ?? '',
          address2: data.address2 ?? '',
          city: data.city ?? '',
          state: data.state ?? '',
          zip_code: data.zip_code ?? '',
        },
        phone_number: data.phone_number ? (parsePhoneNumber(data.phone_number, 'US')?.format('E.164') as string) : '',
        client_ids: resolvedClientIdsForCreate,
        client_division_ids: resolvedClientDivisionIdsForCreate.filter((id) => id > 0),
        ...(data.external_id?.trim() &&
          resolvedClientIdsForCreate.length === 1 && { external_id: data.external_id.trim() }),
      };

      try {
        newUser = await createCollectorUser(createPayload, currentUserRole);
      } catch (e) {
        displayErrorDetails(e);
        setIsSaving(false);
        return;
      }

      if (!newUser) {
        setIsSaving(false);
        return;
      }

      try {
        if (avatarPayload && avatarFile && avatarAction === 'Upload') {
          const res = await registerAvatarMedia(avatarPayload, currentUserRole);
          const mediaId = res.files[0].upload_id;
          await fetch(res.files[0].upload_url, {
            method: 'PUT',
            body: avatarFile,
            headers: { 'content-type': avatarFile.type },
          });
          await confirmAvatarMedia(mediaId, newUser.id, undefined, currentUserRole);
        } else if (avatarAction === 'Remove') {
          await deleteAvatar(newUser.id);
        }

        const insuranceArray: Insurance[] = [];

        const hasPrimaryInsurance = addPrimaryInsurance && Number(data.insurance_carrier_id) > 0;
        const hasSecondaryInsurance = addSecondaryInsurance && Number(data.secondary_insurance_carrier_id) > 0;

        if (hasPrimaryInsurance) {
          try {
            await validationSchemaNewDesign('primary_insurance').validate(data, { abortEarly: false });
          } catch (e) {
            if (e instanceof ValidationError) {
              e.inner.forEach((err) => {
                if (err.path) setError(err.path as any, { type: 'manual', message: err.message });
              });
            }
            displayErrorDetails('Please complete primary insurance information.');
            setIsSaving(false);
            return;
          }

          let frontIds: string[] = [];
          let backIds: string[] = [];
          await Promise.all([
            (frontIds = await uploadMedia(data.front_of_card)),
            (backIds = await uploadMedia(data.back_of_card)),
          ]);

          const primary = updateDataInsuranceNewDesign(data as any) as Insurance;
          primary.front_of_card = mapMediaToInsuranceMedia(frontIds, 'front_of_card');
          primary.back_of_card = mapMediaToInsuranceMedia(backIds, 'back_of_card');
          insuranceArray.push(primary);
        }

        if (hasSecondaryInsurance) {
          try {
            await validationSchemaNewDesign('secondary_insurance').validate(data, { abortEarly: false });
          } catch (e) {
            if (e instanceof ValidationError) {
              e.inner.forEach((err) => {
                if (err.path) setError(err.path as any, { type: 'manual', message: err.message });
              });
            }
            displayErrorDetails('Please complete secondary insurance information.');
            setIsSaving(false);
            return;
          }

          let sFrontIds: string[] = [];
          let sBackIds: string[] = [];
          await Promise.all([
            (sFrontIds = await uploadMedia(data.secondary_front_of_card)),
            (sBackIds = await uploadMedia(data.secondary_back_of_card)),
          ]);

          const secondary = updateDataSecondaryInsuranceNewDesign(data as any) as Insurance;
          secondary.front_of_card = mapMediaToInsuranceMedia(sFrontIds, 'front_of_card');
          secondary.back_of_card = mapMediaToInsuranceMedia(sBackIds, 'back_of_card');
          secondary.is_secondary = true;
          insuranceArray.push(secondary);
        }

        if (insuranceArray.length > 0) {
          await createPatientInsurance({ user_id: newUser.id, insurance: insuranceArray });
        }

        toast.success('Patient created.', { className: 'fs-unmask' });
        roleNavigate('/users', { replace: true });
      } catch (e) {
        displayErrorDetails(e);
      } finally {
        setIsSaving(false);
      }
    },
    [
      sendInvitation,
      avatarPayload,
      avatarFile,
      avatarAction,
      addPrimaryInsurance,
      addSecondaryInsurance,
      allowedCollectorClientIds,
      currentUserRole,
      roleNavigate,
      setError,
    ],
  );

  const isQuickAdd = !sendInvitation && !(watch('email') || '').trim();
  const managingName = [watch('first_name'), watch('last_name')].filter(Boolean).join(' ') || 'this user';
  const selectedClientTagId = watch('client_tag_id');
  const hasCollectorClientTags = clientTagOptions.length > 0;
  const divisionOptions = useMemo(
    () =>
      scopeDivisionOptionsToActor(
        getClientDivisionOptions(allClientTags, selectedClientTagId ? [selectedClientTagId] : []).map((option) => ({
          id: option.id,
          value: String(option.value),
          label: option.label,
        })),
        authUser,
      ),
    [allClientTags, selectedClientTagId, authUser],
  );
  // Division is only required when the actor is themselves confined to a
  // division (not merely whole-tag) -- an unconfined CA/Collector can leave
  // a patient's division unset. A confined actor's own division(s) are the
  // only ones `divisionOptions` ever contains (see `scopeDivisionOptionsToActor`).
  // Also requires the field to actually be rendered (`divisionOptions.length
  // > 0`): a confined actor can still land on a tag with no divisions of
  // its own, and without this the requirement would survive with no field
  // left to satisfy it, permanently blocking submit.
  const divisionRequired =
    Array.isArray(authUser?.divisions) && authUser.divisions.length > 0 && divisionOptions.length > 0;

  // A confined actor with exactly one division has no real choice to make --
  // pre-check it so they don't have to click the only option, while leaving
  // it interactive: they can still uncheck it, which blocks submit (required)
  // until it's re-checked.
  const autoSelectedDivisionRef = useRef<string | null>(null);
  useEffect(() => {
    if (divisionRequired && divisionOptions.length === 1) {
      const onlyValue = divisionOptions[0].value;
      if (autoSelectedDivisionRef.current !== onlyValue) {
        autoSelectedDivisionRef.current = onlyValue;
        setValue('client_division_id', onlyValue, { shouldValidate: true });
      }
    } else {
      autoSelectedDivisionRef.current = null;
    }
  }, [divisionOptions, divisionRequired, setValue]);

  const onInvalid = (errorsList: FieldErrors<FormValues>): void => {
    const error = Object.values(errorsList)[0] as { message?: string } | undefined;
    if (error?.message) {
      displayErrorDetails(error.message);
    }
  };

  const onSecondaryInsuranceToggle = (_event: React.SyntheticEvent, expanded: boolean): void => {
    if (!expanded) {
      Object.keys(defaultInsuranceForm).forEach((k) => {
        if (String(k).startsWith('secondary_')) {
          setValue(k as any, (defaultInsuranceForm as any)[k], { shouldValidate: true });
        }
      });
      setImages((prev: any) => ({
        ...prev,
        secondary_front_of_card: [],
        secondary_back_of_card: [],
      }));
    }
    setAddSecondaryInsurance(expanded);
  };

  const onPrimaryInsuranceToggle = (_event: React.SyntheticEvent, expanded: boolean): void => {
    if (!expanded) {
      Object.keys(defaultInsuranceForm).forEach((k) => {
        if (!String(k).startsWith('secondary_')) {
          setValue(k as any, (defaultInsuranceForm as any)[k], { shouldValidate: true });
        }
      });
      setImages((prev: any) => ({
        ...prev,
        front_of_card: [],
        back_of_card: [],
      }));
      // Secondary insurance depends on primary in this flow.
      setAddSecondaryInsurance(false);
      Object.keys(defaultInsuranceForm).forEach((k) => {
        if (String(k).startsWith('secondary_')) {
          setValue(k as any, (defaultInsuranceForm as any)[k], { shouldValidate: true });
        }
      });
      setImages((prev: any) => ({
        ...prev,
        secondary_front_of_card: [],
        secondary_back_of_card: [],
      }));
    }
    setAddPrimaryInsurance(expanded);
  };

  const isCareAdvisor = authUser.role.name === UserRoleName.CareAdvisor;

  const pageContent = (
    <Box className={addNewUserStyles.page}>
      <Link
        component="button"
        type="button"
        underline="none"
        variant="body2"
        onClick={() => roleNavigate('/users')}
        className={addNewUserStyles.backLink}
      >
        <ChevronLeftIcon className={addNewUserStyles.backChevron} aria-hidden />
        Back
      </Link>

      <Alert severity="info" className={addNewUserStyles.patientProfileNotice} sx={{ mb: 3 }}>
        <AlertTitle>Creating patient profile</AlertTitle>
        You are currently managing {managingName}&apos;s profile.
      </Alert>

      <div className={addNewUserStyles.sendInvitationSection}>
        <span className={addNewUserStyles.sendInvitationLabel} style={{ fontSize: '20px' }}>
          Send invitation to user?
        </span>
        <div className={addNewUserStyles.yesNoToggle}>
          <button
            type="button"
            className={`${addNewUserStyles.yesNoBtn} ${sendInvitation ? addNewUserStyles.yesNoBtnActive : ''}`}
            onClick={() => setSendInvitation(true)}
          >
            Yes
          </button>
          <button
            type="button"
            className={`${addNewUserStyles.yesNoBtn} ${!sendInvitation ? addNewUserStyles.yesNoBtnActive : ''}`}
            onClick={() => setSendInvitation(false)}
          >
            No
          </button>
        </div>
      </div>
      <Divider className={addNewUserStyles.sectionDivider} style={{ marginBottom: '24px' }} />

      <Box className={addNewUserStyles.formRoot}>
        <form noValidate onSubmit={handleSubmit(onSubmit, onInvalid)} className={addNewUserStyles.form}>
          {/* Basic info */}
          <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '20px' }}>
            Basic information
          </Typography>
          <Box className={addNewUserStyles.avatarRow}>
            <ProfileImage
              reviewProfile
              setAvatarFile={setAvatarFile}
              setAvatarPayload={setAvatarPayload}
              setAvatarAction={setAvatarAction}
              triggerProfileAvatar={triggerProfileAvatar}
              setImage={setImage}
              image={image}
              errors={errors}
              size="80px"
              isNewDependent
            />
          </Box>

          <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              {...register('first_name')}
              label="First name"
              required
              value={watch('first_name')}
              onChange={(e) => setValue('first_name', e.target.value, { shouldValidate: true })}
              errors={errors}
              name="first_name"
              wrapperClassName={addNewUserStyles.inputWrap}
            />
            <TextField
              {...register('middle_name')}
              label="Middle name (optional)"
              value={watch('middle_name')}
              onChange={(e) => setValue('middle_name', e.target.value)}
              errors={errors}
              name="middle_name"
              wrapperClassName={addNewUserStyles.inputWrap}
            />
            <TextField
              {...register('last_name')}
              label="Last name"
              required
              value={watch('last_name')}
              onChange={(e) => setValue('last_name', e.target.value, { shouldValidate: true })}
              errors={errors}
              name="last_name"
              wrapperClassName={addNewUserStyles.inputWrap}
            />
          </Box>

          <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              {...register('preferred_name')}
              label="Preferred name (optional)"
              value={watch('preferred_name')}
              onChange={(e) => setValue('preferred_name', e.target.value)}
              name="preferred_name"
              wrapperClassName={addNewUserStyles.inputWrap}
            />
            <TextField
              {...register('date_of_birth')}
              label="Date of birth"
              required
              value={watch('date_of_birth')}
              onChange={(e) => dateAutoSlashes(e, 'date_of_birth', setValue)}
              errors={errors}
              name="date_of_birth"
              maxLength={10}
              wrapperClassName={addNewUserStyles.inputWrap}
            />
          </Box>

          <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              {...register('email')}
              label="Email address"
              type="email"
              required={!isQuickAdd}
              value={watch('email')}
              onChange={(e) => setValue('email', e.target.value, { shouldValidate: true })}
              errors={errors}
              name="email"
              wrapperClassName={addNewUserStyles.inputWrap}
            />
            <Box className={addNewUserStyles.inputWrap}>
              <PhoneInput
                {...register('phone_number')}
                value={watch('phone_number') as E164Number}
                name="phone_number"
                onChange={(v) => setValue('phone_number', v ?? '', { shouldValidate: true })}
                label="Phone number"
                errors={errors}
                wrapperClassName={addNewUserStyles.inputWrap}
              />
            </Box>
          </Box>

          <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box className={addNewUserStyles.inputWrap}>
              <MUISelectInput
                {...register('gender')}
                label="Gender assigned at birth"
                value={watch('gender') || ''}
                onChange={(v: string) => setValue('gender', v, { shouldValidate: true })}
                name="gender"
                options={genderOptions}
                errors={errors}
              />
            </Box>
            <Box className={addNewUserStyles.inputWrap}>
              <MUISelectInput
                {...register('gender_identity')}
                label="Gender identity"
                value={watch('gender_identity') || ''}
                onChange={(v: string) => setValue('gender_identity', v, { shouldValidate: true })}
                name="gender_identity"
                options={genderIdentity}
                errors={errors}
              />
            </Box>
            <Box className={addNewUserStyles.inputWrap}>
              <MUISelectInput
                {...register('pronouns')}
                label="Preferred pronouns (optional)"
                value={watch('pronouns') || ''}
                onChange={(v: string) => setValue('pronouns', v)}
                name="pronouns"
                options={pronounsOptions}
                errors={errors}
              />
            </Box>
          </Box>

          <Box className={addNewUserStyles.row}>
            <MultiSelectInputMUI
              {...register('language')}
              label="Languages"
              name="language"
              withId
              value={
                Array.isArray(watch('language')) ? (watch('language') as OptionLanguage[]).map((l) => String(l.id)) : []
              }
              onChange={(ids: string[]) => {
                const opts = languageOptions.filter((o) => ids.includes(String(o.id)));
                setValue('language', opts as unknown as FormValues['language'], { shouldValidate: true });
              }}
              options={languageOptions}
              errors={errors}
              wrapperClassName={addNewUserStyles.inputWrap}
            />
          </Box>

          <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              {...register('address1')}
              label="Address"
              value={watch('address1')}
              onChange={(e) => setValue('address1', e.target.value)}
              errors={errors}
              name="address1"
              wrapperClassName={addNewUserStyles.inputWrap}
            />
            <TextField
              {...register('address2')}
              label="Apartment, suite, etc. (optional)"
              value={watch('address2')}
              onChange={(e) => setValue('address2', e.target.value)}
              name="address2"
              wrapperClassName={addNewUserStyles.inputWrap}
            />
          </Box>

          <Box className={addNewUserStyles.row} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              {...register('city')}
              label="City"
              value={watch('city')}
              onChange={(e) => setValue('city', e.target.value)}
              errors={errors}
              name="city"
              wrapperClassName={addNewUserStyles.inputWrap}
            />
            <Box className={addNewUserStyles.inputWrap}>
              <MUISelectInput
                {...register('state')}
                label="State"
                value={watch('state') || ''}
                onChange={(v: string) => setValue('state', v, { shouldValidate: true })}
                name="state"
                options={stateOptions}
                errors={errors}
              />
            </Box>
            <TextField
              {...register('zip_code')}
              label="Zip code"
              value={watch('zip_code')}
              onChange={(e) => setValue('zip_code', e.target.value)}
              errors={errors}
              name="zip_code"
              wrapperClassName={addNewUserStyles.inputWrap}
            />
          </Box>

          <Divider className={addNewUserStyles.sectionDivider} />

          {/* Client tags */}
          {hasCollectorClientTags && (
            <>
              <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '20px', mb: 2 }}>
                Client tags
                {clientTagsRequired && (
                  <Typography component="span" color="error">
                    {' '}
                    *
                  </Typography>
                )}
              </Typography>
              <p className={addNewUserStyles.clientTagsDescription}>
                Select your collector client tag for this patient.
              </p>
              <div className={addNewUserStyles.inputWrap}>
                <MUISelectInput
                  name="client_tag_id"
                  label="Client tag"
                  required={clientTagsRequired}
                  value={selectedClientTagId || ''}
                  options={clientTagOptions}
                  onChange={(value: string) => {
                    setValue('client_tag_id', value, { shouldValidate: true });
                    setValue('client_division_id', '', { shouldValidate: true });
                  }}
                />
              </div>
            </>
          )}

          {selectedClientTagId && divisionOptions.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '20px', mb: 2, mt: 2 }}>
                Division
                {divisionRequired && (
                  <Typography component="span" color="error">
                    {' '}
                    *
                  </Typography>
                )}
              </Typography>
              <div className={addNewUserStyles.inputWrap}>
                <MUISelectInput
                  name="client_division_id"
                  label="Division"
                  required={divisionRequired}
                  value={watch('client_division_id') || ''}
                  options={divisionOptions}
                  onChange={(value: string) => setValue('client_division_id', value, { shouldValidate: true })}
                />
              </div>
            </>
          )}

          {selectedClientTagId && (
            <>
              <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '20px', mb: 2, mt: 2 }}>
                User IDs
              </Typography>
              <Box className={addNewUserStyles.inputWrap}>
                <TextField
                  {...register('external_id')}
                  label="External User ID"
                  value={watch('external_id') || ''}
                  onChange={(e) => setValue('external_id', e.target.value, { shouldValidate: true })}
                  errors={errors}
                  name="external_id"
                  maxLength={40}
                  wrapperClassName={addNewUserStyles.inputWrap}
                />
              </Box>
            </>
          )}

          <Divider className={addNewUserStyles.sectionDivider} />

          {/* Insurance information (full) */}
          <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '20px' }}>
            Insurance information
          </Typography>

          <Divider className={addNewUserStyles.sectionDivider} />

          <Box>
            <Accordion
              expanded={addPrimaryInsurance}
              onChange={onPrimaryInsuranceToggle}
              className={addNewUserStyles.insuranceAccordion}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon className={addNewUserStyles.insuranceAccordionExpandIcon} />}
                className={addNewUserStyles.insuranceAccordionSummary}
              >
                <Typography className={addNewUserStyles.insuranceAccordionTitle}>
                  Primary insurance information
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={addNewUserStyles.insuranceAccordionDetails}>
                <InsuranceFormSection
                  hideTitle
                  isSecondary={false}
                  allowNoInsuranceCarrier
                  phoneInputRef={phoneInputRef}
                  register={register as any}
                  errors={errors as any}
                  setValue={setValue as any}
                  watch={watch as any}
                  getValues={getValues as any}
                  handleChange={handleChange}
                  handleDragOver={handleDragOver}
                  handleDrop={handleDrop}
                  handleDragEnter={handleDragEnter}
                  handleDragLeave={handleDragLeave}
                  isDragging={isDragging}
                  deleteMedia={deleteMedia}
                  images={images as any}
                  profileAddressSource={{
                    address1: watch('address1') || '',
                    address2: watch('address2') || '',
                    city: watch('city') || '',
                    state: watch('state') || '',
                    zip_code: watch('zip_code') || '',
                  }}
                />
              </AccordionDetails>
            </Accordion>

            {addPrimaryInsurance && (
              <Accordion
                expanded={addSecondaryInsurance}
                onChange={onSecondaryInsuranceToggle}
                className={addNewUserStyles.insuranceAccordion}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon className={addNewUserStyles.insuranceAccordionExpandIcon} />}
                  className={addNewUserStyles.insuranceAccordionSummary}
                >
                  <Typography className={addNewUserStyles.insuranceAccordionTitle}>
                    Secondary insurance information
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={addNewUserStyles.insuranceAccordionDetails}>
                  <InsuranceFormSection
                    hideTitle
                    isSecondary
                    allowNoInsuranceCarrier
                    phoneInputRef={phoneInputRef}
                    register={register as any}
                    errors={errors as any}
                    setValue={setValue as any}
                    watch={watch as any}
                    getValues={getValues as any}
                    handleChange={handleChange}
                    handleDragOver={handleDragOver}
                    handleDrop={handleDrop}
                    handleDragEnter={handleDragEnter}
                    handleDragLeave={handleDragLeave}
                    isDragging={isDragging}
                    deleteMedia={deleteMedia}
                    images={images as any}
                    profileAddressSource={{
                      address1: watch('address1') || '',
                      address2: watch('address2') || '',
                      city: watch('city') || '',
                      state: watch('state') || '',
                      zip_code: watch('zip_code') || '',
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            )}
          </Box>

          <Box className={addNewUserStyles.actions} sx={{ mt: 4 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving}
              className={addNewUserStyles.saveChangesButton}
            >
              {isSaving ? 'Saving…' : 'Save changes'}
            </Button>
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="none"
              onClick={() => roleNavigate('/users')}
              className={addNewUserStyles.cancelLink}
            >
              Cancel
            </Link>
          </Box>
        </form>
      </Box>
    </Box>
  );

  return isCareAdvisor ? (
    pageContent
  ) : (
    <Container
      patientDashboard
      childClassName={styles.dashboard}
      containerStyle={{ overflowY: 'auto' }}
      childrenStyle={{ alignItems: 'flex-start', width: '100%' }}
    >
      {pageContent}
    </Container>
  );
};

export default AddNewUserPage;
